import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Building2 } from "lucide-react";
import api, { IMG, COMPANY } from "@/lib/api";
import { useImage } from "@/context/MediaContext";
import { Section, PrimaryButton, GlassCard, Field, inputCls, fadeUp, Toast } from "@/components/ui-kit";

export default function Investors() {
  const techImg = useImage("tech_bg", IMG.network);
  const founderImg = useImage("founder", null);
  const [form, setForm] = useState({ name: "", email: "", organization: "", investment_range: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/investor-inquiries", form); setSuccess(true); }
    catch { setToast("Could not submit. Please email gkfleet25@gmail.com directly."); }
    finally { setLoading(false); setTimeout(() => setToast(""), 3500); }
  };

  return (
    <div data-testid="investors-page">
      <section className="relative pt-32 pb-20 overflow-hidden border-b border-white/5">
        <img src={techImg} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/90 to-[#0A0A0A]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <motion.p {...fadeUp} className="text-xs tracking-[0.3em] uppercase text-[#3B82F6]">Investor Relations</motion.p>
          <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="mt-5 font-display text-5xl md:text-7xl tracking-tighter font-semibold leading-[1.02] max-w-4xl">
            A scalable mobility<br/>asset platform.
          </motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-6 max-w-2xl text-lg text-[#D1D5DB]">
            GK Fleet is building India's most driver-focused fleet operator. We're targeting 100+ vehicles by 2030 — backed by disciplined unit economics and durable driver retention.
          </motion.p>
        </div>
      </section>

      <Section eyebrow="Company Overview" title="A focused operator, not a marketplace.">
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <motion.div {...fadeUp} className="md:col-span-7 space-y-4 text-[#D1D5DB] leading-relaxed text-lg">
            <p>GK Fleet Private Limited owns and operates vehicles that are rented to professional drivers operating on Uber, Ola, and Rapido. We are not a marketplace, not a self-drive rental, and not a tourist taxi service.</p>
            <p>Our thesis is simple: fleet operators that genuinely serve drivers retain them longer, run their vehicles more, and compound advantage over years.</p>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="md:col-span-5 grid grid-cols-2 gap-3">
            {[{ k: "Founded", v: "2025" }, { k: "HQ", v: "Hyderabad" }, { k: "Fleet today", v: "7" }, { k: "Uptime", v: "75%" }, { k: "Target 2030", v: "100+" }, { k: "Primary vehicle", v: "WagonR" }].map((x) => (
              <div key={x.k} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <p className="text-xs tracking-[0.18em] uppercase text-[#9CA3AF]">{x.k}</p>
                <p className="mt-1 font-display text-xl">{x.v}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </Section>

      <Section eyebrow="Growth Strategy" title="Three engines compounding over five years.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { t: "Driver retention", d: "Best-in-class partner experience drives multi-year tenure and reduces re-onboarding cost." },
            { t: "Asset utilization", d: "Targeting 85%+ uptime through proactive service operations and rapid swap inventory." },
            { t: "Vehicle financing", d: "Disciplined unit economics enable scaling fleet additions with stable cash flow." },
          ].map((p, i) => (
            <motion.div key={p.t} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }}>
              <GlassCard className="h-full">
                <h3 className="font-display text-xl">{p.t}</h3>
                <p className="mt-3 text-[#9CA3AF] leading-relaxed">{p.d}</p>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Market Opportunity" title="India's mobility market is structurally driver-constrained.">
        <div className="grid md:grid-cols-12 gap-8">
          <motion.div {...fadeUp} className="md:col-span-7 space-y-4 text-[#D1D5DB] text-lg leading-relaxed">
            <p>Demand for ride-hail trips continues to outpace supply of reliable drivers and well-maintained vehicles. Fleet operators that solve the driver side of this equation win disproportionately.</p>
            <p>Hyderabad alone has hundreds of thousands of active mobility users, and the surrounding Tier-2 corridor expands the addressable market significantly.</p>
          </motion.div>
          <motion.div {...fadeUp} className="md:col-span-5">
            <GlassCard>
              <p className="text-xs tracking-[0.2em] uppercase text-[#9CA3AF]">Why now</p>
              <ul className="mt-4 space-y-3 text-[#D1D5DB]">
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[#3B82F6] flex-shrink-0" /> Mobility demand growing double-digits YoY</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[#3B82F6] flex-shrink-0" /> Driver supply is the binding constraint</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[#3B82F6] flex-shrink-0" /> Compact, fuel-efficient vehicles win economics</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-[#3B82F6] flex-shrink-0" /> Operator concentration in India is low</li>
              </ul>
            </GlassCard>
          </motion.div>
        </div>
      </Section>

      <Section eyebrow="Founder" title="Led by V. Karthik Neal.">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#1A1A1A] aspect-[4/5]">
              {founderImg ? (
                <img src={founderImg} alt="V. Karthik Neal — Founder & Director" className="w-full h-full object-cover" data-testid="founder-portrait" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  <div className="w-20 h-20 rounded-full bg-[#3B82F6]/10 border border-[#3B82F6]/30 flex items-center justify-center font-display text-3xl text-[#3B82F6]">VK</div>
                  <p className="mt-4 text-sm text-[#9CA3AF]">Founder portrait coming soon.</p>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/90 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="text-xs tracking-[0.2em] uppercase text-[#3B82F6]">Founder & Director</p>
                <p className="font-display text-2xl mt-1">V. Karthik Neal</p>
              </div>
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="lg:col-span-7 space-y-5 text-[#D1D5DB] text-lg leading-relaxed">
            <p>GK Fleet is founded and led by V. Karthik Neal — driven by a single conviction: the next generation of mobility leaders will be the ones who treat drivers as partners, not numbers.</p>
            <p>Based in Hyderabad, Karthik is building GK Fleet for the long term — disciplined unit economics, sustainable growth, and a culture that values every partner who turns the key.</p>
          </motion.div>
        </div>
      </Section>

      <Section id="inquiry" eyebrow="Investor Inquiry" title="Let's talk.">
        <div className="grid lg:grid-cols-2 gap-10">
          <motion.div {...fadeUp} className="space-y-5 text-[#D1D5DB] leading-relaxed">
            <p className="text-lg">We're early — but disciplined. If you are an investor or strategic partner who thinks about mobility, fleet, or asset-heavy operating businesses in India, we'd love to share more.</p>
            <div className="flex items-center gap-3 text-sm">
              <Building2 className="w-4 h-4 text-[#3B82F6]" /> GK Fleet Private Limited · Hyderabad
            </div>
          </motion.div>

          {success ? (
            <GlassCard data-testid="investor-success" className="text-center py-12">
              <CheckCircle2 className="w-12 h-12 text-[#3B82F6] mx-auto" />
              <h3 className="mt-5 font-display text-2xl">Thank you.</h3>
              <p className="mt-2 text-[#D1D5DB]">Our founder will reach out within 5 business days.</p>
            </GlassCard>
          ) : (
            <form onSubmit={onSubmit} data-testid="investor-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name"><input required className={inputCls} data-testid="inv-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Email"><input required type="email" className={inputCls} data-testid="inv-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Organization"><input className={inputCls} data-testid="inv-org" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} /></Field>
              <Field label="Investment range">
                <select className={inputCls} data-testid="inv-range" value={form.investment_range} onChange={(e) => setForm({ ...form, investment_range: e.target.value })}>
                  <option value="">Select…</option>
                  <option>Angel / Pre-Seed (₹10L – ₹1Cr)</option>
                  <option>Seed (₹1Cr – ₹5Cr)</option>
                  <option>Series A+</option>
                  <option>Strategic / Other</option>
                </select>
              </Field>
              <div className="md:col-span-2">
                <Field label="Message">
                  <textarea rows={4} className={inputCls} data-testid="inv-msg" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <PrimaryButton data-testid="inv-submit" disabled={loading}>{loading ? "Sending…" : "Submit inquiry"}</PrimaryButton>
              </div>
            </form>
          )}
        </div>
      </Section>

      <Toast msg={toast} />
    </div>
  );
}
